/** @format */
import { useTranslation } from "react-i18next";
import ExportPdfIcon from "../assets/icons/ExportPdfIcon.svg?react";
import SaveLibraryIcon from "../assets/icons/SaveLibraryIcon.svg?react";
import { trackEvent } from "../utils/analytics";

import { useState } from "react";
import { hasDisplayableContent, type Brand, type Question } from "@question-bank/shared";
import { buildPdfContentTree } from "../features/pdfExport/buildPdfContentTree";
import { fetchContentImageMap, fetchImageAsDataUri } from "../features/pdfExport/prefetchImages";
import { downloadPdf, downloadUrl, toFilename } from "../features/pdfExport/downloadPdf";
import { labelledQuestions, resolveActiveLang, toStringArray, type LabelledQuestion } from "../utils/helpers";
import type { MarkSchemeImage, PdfContentTree, PdfPages } from "../features/pdfExport/types";
import { useBrand } from "../hooks/useBrand";
import type { PaperGroup } from "../hooks/usePaperQuestions";
import { loadPapers, usePapersWithAttachments } from "../hooks/usePapersWithAttachments";
import { getAssetUrl } from "../api/questions";
import {
	collectAttachmentDownloads,
	hasAdditionalResources,
	paperIdOf,
	type AttachmentLanguage,
} from "../utils/attachments";
import { flipLang, isWelshLang, toLangCode, type ExportLanguage } from "../utils/language";
const pStyles = "font-lato text-(--public-color-text-default) text-[16px] leading-[175%]";

const divInputStyles = "flex items-start gap-2";
const inputStyles = "custom-check shrink-0 mt-0.5";
const btnStyles =
	" flex flex-row cursor-pointer w-full h-[55px] rounded-[6px] py-[6px] px-3 border font-roboto font-normal text-[16px] leading-[150%] mx-auto gap-2 items-center justify-center";

const SECOND_DOWNLOAD_DELAY_MS = 500;
const paperPages: PdfPages = {
	titlePage: true,
	questions: true,
	examinerComments: false,
	markingScheme: false,
};

const extrasPagesBase: PdfPages = {
	titlePage: true,
	questions: false,
	examinerComments: false,
	markingScheme: false,
};

const resolveMarkSchemeKey = (
	question: Question,
	exportLang: ExportLanguage,
): { key: string; isFallback: boolean } | null => {
	const ownKey = (isWelshLang(exportLang) ? question.cy : question.en)?.markSchemeImage;

	const otherKey = (isWelshLang(exportLang) ? question.en : question.cy)?.markSchemeImage;
	if (ownKey) {
		return { key: ownKey, isFallback: false };
	}
	return otherKey ? { key: otherKey, isFallback: true } : null;
};

const fetchMarkSchemeImageMap = async (
	items: LabelledQuestion[],
	exportLang: ExportLanguage,
	brand: Brand,
): Promise<Map<string, MarkSchemeImage>> => {
	const entries = await Promise.all(
		items.map(async ({ question }) => {
			const resolved = resolveMarkSchemeKey(question, exportLang);
			const image = await fetchImageAsDataUri(resolved ? getAssetUrl(brand, resolved.key) : null);
			return [question.id, { dataUri: image?.dataUri ?? null, isFallBack: resolved?.isFallback ?? false }] as const;
		}),
	);
	return new Map(entries);
};

const buildQuestionsTree = async (
	items: LabelledQuestion[],
	paperTitle: string,
	lang: ExportLanguage,
): Promise<PdfContentTree> => {
	const langCode = toLangCode(lang);
	const contentImageUris = await fetchContentImageMap(
		items.map((item) => item.question),
		langCode,
	);
	return buildPdfContentTree(items, paperTitle, lang, new Map(), paperPages, undefined, contentImageUris);
};

interface PaperActionsProps {
	groups: PaperGroup[];
	title: string;
}

export const PaperActions = ({ groups, title }: PaperActionsProps) => {
	const { t, i18n } = useTranslation();
	const [paperName, setPaperName] = useState("");
	const { brand } = useBrand();
	const [isMarkSchemeTicked, setIsMarkSchemeTicked] = useState(false);
	const [isExaminersCommentsTicked, setIsExaminersCommentsTicked] = useState(false);
	const [isGenerating, setIsGenerating] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [hasIncludeAttachments, setHasIncludeAttachments] = useState(false);
	const [isWelshPaper, setIsWelshPaper] = useState(false);
	const paperIdsWithAttachments = usePapersWithAttachments(brand);

	const activeLang = resolveActiveLang(i18n.language);

	//Mirrors labelled questions filtering: the labelled items themselves are built per export,
	//because the welsh checkbox decides the labelling language at download time whether a flipped language pass also runs
	const hasExportableQuestions = groups.some((group) => group.questions.some(hasDisplayableContent));

	const hasAvailableAttachments = groups.some((group) =>
		group.questions.some((question) => hasAdditionalResources(question, paperIdsWithAttachments)),
	);

	// Default export name: first cart subject + today's date with no locale argument, so each user
	// sees their own regional format (e.g "Business - 23/07/2026" for en-GB).
	const firstSubject = toStringArray(groups[0]?.entry.subject)[0];
	const defaultPaperName = firstSubject ? `${firstSubject} - ${new Date().toLocaleDateString()}` : title;

	// Bundle the papers' PDF attachments alongside the generated paper. Papers
	// referenced by multiple questions contribute their assets only once, and
	// each requested language is downloaded once (deduped by asset key).
	const bundleAttachments = async () => {
		const baseLangCode: AttachmentLanguage = toLangCode(activeLang);
		const flippedLangCode: AttachmentLanguage = baseLangCode === "cy" ? "en" : "cy";
		//A ticked language box exports adds flipped-language documents to the export, so that language's
		// attachment files go along with the base lang
		const languages = Array.from(
			new Set<AttachmentLanguage>([baseLangCode, ...(isWelshPaper ? [flippedLangCode] : [])]),
		);
		const papers = await loadPapers(brand);
		const papersById = new Map(papers.map((paper) => [paper.id, paper]));
		const questionPaperIds = groups.flatMap((group) => group.questions).map((question) => paperIdOf(question));
		const downloads = collectAttachmentDownloads(papersById, questionPaperIds, languages);
		for (const download of downloads) {
			await downloadUrl(getAssetUrl(brand, download.assetKey), download.filename);
		}
	};

	const handleExport = async () => {
		// one items value per language pass: every surface of a pass  (content tree, mark-scheme
		// fetch, marks table) must share that pass's language same language-generated labels, so the added
		// flipped-language paper below builds its own list rather than reusing this one.
		const items: LabelledQuestion[] = groups.flatMap((group, groupIndex) =>
			labelledQuestions(group.questions, group.entry, groupIndex, activeLang),
		);
		if (items.length === 0) {
			return;
		}
		trackEvent("export_pdf");
		setError(null);
		setIsGenerating(true);
		try {
			const paperTitle = paperName.trim() || defaultPaperName;
			const filenameBase = toFilename(paperTitle);

			const questionsTree = await buildQuestionsTree(items, paperTitle, activeLang);
			await downloadPdf(questionsTree, `${filenameBase}.pdf`);

			const flippedLang = flipLang(activeLang);
			const flippedItems: LabelledQuestion[] = groups.flatMap((group, groupIndex) =>
				labelledQuestions(group.questions, group.entry, groupIndex, flippedLang),
			);
			const filenamePrefix = isWelshLang(flippedLang) ? "Welsh" : "English";

			if (isWelshPaper) {
				// The ticked box adds a paper in the flipped language rather than replacing the base one;
				// only the questions document is duplicated, the extras below stay in the site language.

				const flippedTree = await buildQuestionsTree(flippedItems, paperTitle, flippedLang);
				await new Promise((resolve) => setTimeout(resolve, SECOND_DOWNLOAD_DELAY_MS));
				await downloadPdf(flippedTree, `${filenamePrefix}_${filenameBase}.pdf`);
			}
			const extraSections: { suffix: string; pages: PdfPages }[] = [];
			if (isMarkSchemeTicked) {
				extraSections.push({ suffix: "_MarkScheme", pages: { ...extrasPagesBase, markingScheme: true } });
			}
			if (isExaminersCommentsTicked) {
				extraSections.push({
					suffix: "_ExaminersComments",
					pages: { ...extrasPagesBase, examinerComments: true },
				});
			}

			const downloadExtras = async (passItems: LabelledQuestion[], lang: ExportLanguage, prefix: string) => {
				// Only the marking scheme section renders these images, so only fetch them when it's ticked.
				const markSchemeImageUris = isMarkSchemeTicked
					? await fetchMarkSchemeImageMap(passItems, lang, brand)
					: new Map<string, MarkSchemeImage>();
				for (const { suffix, pages } of extraSections) {
					const extrasTree = buildPdfContentTree(passItems, paperTitle, lang, markSchemeImageUris, pages);
					await new Promise((resolve) => setTimeout(resolve, SECOND_DOWNLOAD_DELAY_MS));
					await downloadPdf(extrasTree, `${prefix}${filenameBase}${suffix}.pdf`);
				}
			};
			if (extraSections.length > 0) {
				await downloadExtras(items, activeLang, "");
				if (isWelshPaper) {
					await downloadExtras(flippedItems, flippedLang, `${filenamePrefix}_`);
				}
			}
			// Re-check availability: the tick survives in state after the checkbox row hides
			// (e.g the last attachment-holding question left the cart), and a stale tick must not bundle anything.
			if (hasIncludeAttachments && hasAvailableAttachments) {
				await bundleAttachments();
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : t("pdfExport.errorGeneric"));
		} finally {
			setIsGenerating(false);
		}
	};

	return (
		<div className="flex flex-col bg-white rounded-[10px] h-fit w-full border-2 border-(--public-color-border-default) ml-5 mr-5 mt-8 mb-5 py-5.25 px-7.75 gap-5.5">
			<header className="text-[20px] leading-[150%] font-lato font-semibold">{t("paperActions.title")}</header>

			<div className="flex flex-col gap-4.5 mb-3">
				<button
					data-testid="btn-export-paper"
					type="button"
					onClick={handleExport}
					disabled={isGenerating || !hasExportableQuestions}
					aria-busy={isGenerating}
					className={`${btnStyles} bg-(--public-color-theme-primary) border-(--public-color-theme-primary) text-white`}
				>
					<ExportPdfIcon />

					{t("paperActions.exportPdf")}
				</button>
				<button
					type="button"
					disabled
					title={t("paperActions.comingSoon")}
					className={`${btnStyles} bg-(--public-color-btn-save-library) border-(--public-color-btn-save-library) cursor-not-allowed opacity-60`}
				>
					<SaveLibraryIcon />
					{t("paperActions.saveToLibrary")}
				</button>
			</div>

			<div className="flex flex-col gap-1.25">
				<label htmlFor="paper-name" className={pStyles}>
					{t("paperActions.paperNameLabel")}
				</label>
				<input
					id="paper-name"
					type="text"
					value={paperName}
					placeholder={defaultPaperName}
					onChange={(event) => setPaperName(event.target.value)}
					className="w-full rounded-md border border-(--public-color-border-default) px-3 py-1.5 font-lato text-[16px]"
				/>
			</div>

			<div className="flex flex-col gap-1.25">
				<div className={divInputStyles}>
					<input
						id="mark-scheme"
						type="checkbox"
						className={inputStyles}
						checked={isMarkSchemeTicked}
						onChange={(event) => setIsMarkSchemeTicked(event.target.checked)}
					/>

					<label aria-label="mark scheme checkbox" htmlFor="mark-scheme" className={pStyles}>
						{t("paperActions.addMarkScheme")}
					</label>
				</div>

				<div className={divInputStyles}>
					<input
						id="examiners-comments"
						type="checkbox"
						className={inputStyles}
						checked={isExaminersCommentsTicked}
						onChange={(event) => setIsExaminersCommentsTicked(event.target.checked)}
					/>

					<label aria-label="examiners comments checkbox" htmlFor="examiners-comments" className={pStyles}>
						{t("paperActions.addExaminersComments")}
					</label>
				</div>

				<div className={divInputStyles}>
					<input
						id={activeLang === "cymraeg" ? "english-paper" : "welsh-paper"}
						type="checkbox"
						className={inputStyles}
						checked={isWelshPaper}
						onChange={(event) => setIsWelshPaper(event.target.checked)}
					/>
					<label
						aria-label={activeLang === "cymraeg" ? "english paper checkbox" : "welsh paper checkbox"}
						htmlFor={activeLang === "cymraeg" ? "english-paper" : "welsh-paper"}
						className={pStyles}
					>
						{activeLang === "cymraeg" ? t("paperActions.englishPaper") : t("paperActions.welshPaper")}
					</label>
					<button type="button" aria-label={t("paperActions.moreInfo")}>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth={2}
							stroke="#333333"
							aria-hidden="true"
							focusable="false"
							className="size-4.75"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z"
							/>
						</svg>
					</button>
				</div>

				{hasAvailableAttachments && (
					<div className={divInputStyles}>
						<input
							id="attachments"
							type="checkbox"
							className={inputStyles}
							checked={hasIncludeAttachments}
							onChange={(event) => setHasIncludeAttachments(event.target.checked)}
						/>
						<label aria-label="attachments checkbox" htmlFor="attachments" className={pStyles}>
							{t("paperActions.addAttachments")}
						</label>
						<button type="button" aria-label={t("paperActions.moreInfo")}>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={2}
								stroke="#333333"
								aria-hidden="true"
								focusable="false"
								className="size-4.75"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z"
								/>
							</svg>
						</button>
					</div>
				)}
			</div>
			{error && (
				<p role="alert" className="text-sm text-red-600">
					{error}
				</p>
			)}
		</div>
	);
};
